import { useState } from "react";
import Skeleton from "react-loading-skeleton";
import styled from "styled-components";
import { IconButton } from "../../../components/buttons";
import { CountrySelect, FormCheck, FormInput, FormSelect } from "../../../components/forms";
import { StandardTable } from "../../../components/tables";
import useAddressDetails from "../hooks/useAddressDetails";
import AddressInput from "./AddressInput";
import BasicFetchError from "./BasicFetchError";
import DetailsWrapper from "./DetailsWrapper";
import EditAddressModal from "./EditAddressModal";
import { openConfirmModal } from "../../../components/modals/ConfirmModal";

const ActiveColumn = styled.td`
    width: 70px;

    && > div.row {
        justify-content: center;
    }

    && > div.row > div {
        margin: 0;
    }
`;

const DefaultColumn = styled.td`
    width: 78px;

    && > div.row {
        justify-content: center;
    }

    && > div.row > div {
        margin: 0;
    }
`;

const AddressDetailsTableEmptyRow = ({ columns, onCreateClicked, ...rest }) => {
    return <tr {...rest}>
        <td colSpan={columns.length}>
            There are no addresses available for this account.
            You can click <a href="#" onClick={onCreateClicked}>here</a> to create one.
        </td>
    </tr>
};

const AddressDetailsTableLoadingRow = ({ columns, loadingLines, ...rest }) => {
    return [...Array(loadingLines)].map((_, i) => (
        <tr key={`loading-${i}`} {...rest}>
            <td colSpan={columns.length}>
                <Skeleton count={1} height={37} />
            </td>
        </tr>
    ));
};

const AddressDetailsTableRow = ({ allowDelete = true, columns, data, onDefaultChanged, onDeleteClicked, onFieldChanged, onEditAddressClicked, ...rest }) => {
    const { address, addressId, countryCode, isActiveAddress, isDefault, postcode, type } = data;

    return <tr {...rest}>
        <td className="col-2 pb-0">
            <FormSelect
                defaultValue={type}
                options={[
                    { label: 'Main Residence', value: 0 },
                    { label: 'Holiday Home', value: 1 },
                    { label: 'Residential Property', value: 2 },
                    { label: 'Commercial Property', value: 3 }
                ]}
                onChange={(values) => onFieldChanged(addressId, 'type', values.value)}
            />
        </td>
        <td className="pb-0">
            <AddressInput
                address={address}
                onEditClick={() => onEditAddressClicked(data)}
            />
        </td>
        <td className="col-1 pb-0">
            <FormInput
                value={postcode}
                placeholder="Postcode"
                onBlur={(value) => onFieldChanged(addressId, 'postcode', value)}
            />
        </td>
        <td className="col-2 pb-0">
            <CountrySelect
                defaultValue={countryCode}
                onBlur={(values) => onFieldChanged(addressId, 'countryCode', values.value)}
            />
        </td>
        <ActiveColumn className="align-middle text-center pb-0">
            <FormCheck
                type="checkbox"
                isChecked={isActiveAddress}
                onChange={() => onFieldChanged(addressId, 'isActiveAddress', !isActiveAddress)}
            />
        </ActiveColumn>
        <DefaultColumn className="align-middle text-center pb-0">
            <FormCheck
                type="radio"
                name="isAddressDefaultRadio"
                isChecked={isDefault}
                onChange={() => onDefaultChanged(addressId)}
            />
        </DefaultColumn>
        {allowDelete === true && (
            <td className="col-md-auto pb-0">
                <IconButton icon="fa-trash-can" variant="danger" onClick={() => onDeleteClicked(data)} />
            </td>
        )}
    </tr>;
};

const getDeleteWarningMessage = (address, postcode) => {
    if (!address) {
        return "Are you sure you want to delete this address?";
    }
    if (!postcode) {
        return `Are you sure you want to delete the address "${address}"?`;
    }

    return `Are you sure you want to delete the address "${address}, ${postcode}"?`;
};

const AddressDetails = ({ masterAccountId, loadingLines = 2 }) => {
    const {
        createAddress,
        data,
        deleteAddress,
        error,
        isCreating,
        isError,
        isLoading,
        isUpdating,
        reload,
        setDefaultAddress,
        updateAddress
    } = useAddressDetails(masterAccountId);

    const onCreateAddressPressed = (e) => createAddress(e);

    const onDefaultAddressChanged = (addressId) => setDefaultAddress(addressId);

    const onDeleteAddressPressed = (addressToDelete) => {
        const { addressId, address, postcode } = addressToDelete;
        openConfirmModal({
            dialogTitle: "Delete Address?",
            message: getDeleteWarningMessage(address, postcode),
            confirmButtonText: "Delete",
            confirmButtonVariant: 'danger'
        }).then(
            (result) => {
                if (result === true) {
                    deleteAddress(addressId)
                }
            },
            () => {}
        );
    }

    const onFieldChanged = (addressId, property, value) => updateAddress(addressId, property, value);

    const [editAddress, setEditAddress] = useState(null);
    const onEditAddressClicked = (address) => setEditAddress(address);

    if (error || isError === true) {
        return <BasicFetchError error={error} retryCallback={reload} />
    }

    return <DetailsWrapper label="Addresses" collapsedByDefault={true}>
        <EditAddressModal
            address={editAddress}
            setAddress={setEditAddress}
            onFieldChanged={(property, value) => onFieldChanged(editAddress.addressId, property, value)}
            isUpdating={isUpdating}
        />
        <StandardTable
            className="table-borderless"
            columns={[
                { label: 'Address Type', property: 'typeDescription' },
                { label: 'Address', property: 'address' },
                { label: 'Postcode', property: 'postcode' },
                { label: 'Country', property: 'countryCode' },
                {
                    label: 'Active',
                    property: 'isActiveAddress',
                    headerClassName: 'text-center'
                },
                {
                    label: 'Default',
                    property: 'isDefault',
                    headerClassName: 'text-center'
                },
                {
                    label: '',
                    property: '',
                    headerRenderer: (props) => (
                        <td key={props.key} style={{ width: '32px' }}>
                            <IconButton icon="fa-plus" variant="success" onClick={onCreateAddressPressed} />
                        </td>
                    )
                }
            ]}
            data={data}
            error={error}
            isCreating={isCreating}
            isLoading={isLoading}
            EmptyRowComponent={AddressDetailsTableEmptyRow}
            emptyRowComponentProps={{ onCreateClicked: onCreateAddressPressed }}
            hideHeaderOnEmpty={true}
            LoadingRowComponent={AddressDetailsTableLoadingRow}
            loadingRowComponentProps={{ loadingLines }}
            RowComponent={AddressDetailsTableRow}
            rowComponentProps={{
                allowDelete: true,
                onDefaultChanged: onDefaultAddressChanged,
                onDeleteClicked: onDeleteAddressPressed,
                onFieldChanged,
                onEditAddressClicked
            }}
        />
    </DetailsWrapper>
};

export default AddressDetails;